import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveAbs = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
};

/**
 * ProductImageCarousel — used on the public Shop grid to render either a
 * single image (legacy products) or a hover-navigable multi-image carousel
 * (products with images[].length > 1). Per-image caption overlay falls back
 * to the legacy single `imageOverlay` for slot 0 if `imageOverlays` is empty.
 *
 * The carousel state is local: hovering the card shows arrows + dot
 * indicators. Arrow / dot clicks change the active image. The component is
 * **purely visual** — it does not call addToCart. The parent retains its own
 * Add-to-Cart button.
 */
export const ProductImageCarousel = ({ product, className = '' }) => {
  const images = (product.images && product.images.length > 0)
    ? product.images
    : (product.image ? [product.image] : []);
  const overlays = product.imageOverlays && product.imageOverlays.length > 0
    ? product.imageOverlays
    : (product.imageOverlay ? [product.imageOverlay] : []);

  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);

  // If product changes (e.g. category filter swaps the list), reset the index.
  useEffect(() => { setActive(0); }, [product.id]);

  const total = images.length;
  const showArrows = total > 1 && hovered;
  const showDots = total > 1;
  const overlayText = overlays[active] || '';

  const go = (e, dir) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setActive((i) => (i + dir + total) % total);
  };

  const goTo = (e, idx) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setActive(idx);
  };

  return (
    <div
      className={`relative h-80 overflow-hidden bg-gray-100 ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`shop-carousel-${product.id}`}
    >
      {total === 0 ? (
        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No image</div>
      ) : (
        images.map((url, i) => (
          <img
            key={i}
            src={cldPresets.productCard(resolveAbs(url))}
            alt={`${product.name} — image ${i + 1}`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === active ? 'opacity-100' : 'opacity-0'
            } ${hovered ? 'scale-110' : 'scale-100'} transition-transform duration-700`}
          />
        ))
      )}

      {overlayText && (
        <div className="absolute inset-x-0 bottom-0 px-4 py-2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none z-10">
          <p
            className="text-white font-semibold text-sm drop-shadow"
            data-testid={`shop-overlay-${product.id}`}
          >
            {overlayText}
          </p>
        </div>
      )}

      {showArrows && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={(e) => go(e, -1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center transition"
            data-testid={`shop-carousel-prev-${product.id}`}
          >
            <ChevronLeft className="h-5 w-5" style={{ color: '#00CED1' }} />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(e) => go(e, 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center transition"
            data-testid={`shop-carousel-next-${product.id}`}
          >
            <ChevronRight className="h-5 w-5" style={{ color: '#00CED1' }} />
          </button>
        </>
      )}

      {showDots && (
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-20"
          data-testid={`shop-carousel-dots-${product.id}`}
        >
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to image ${i + 1}`}
              onClick={(e) => goTo(e, i)}
              className={`h-1.5 rounded-full transition-all ${
                i === active ? 'w-5 bg-white' : 'w-1.5 bg-white/60 hover:bg-white/90'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
